import { useCallback, useState } from 'react'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { LinkButton } from '@/atoms/Button/LinkButton'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { AsH2, HeadingLG, LabelMD, ParagraphLG, ParagraphMD, ParagraphSM, ParagraphXL } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { DocumentCard } from '@/molecules/DocumentCard'
import { EmbeddedVideo as Video } from '@/molecules/EmbeddedVideo'
import { PressHeader } from '@/organisms/PressHeader'
import { TeamMemberDisplay } from '@/organisms/TeamMemberDisplay/TeamMemberDisplay'
import { options } from '@/services/ContentfulService'
import { Downloadable, PressRelease, Project, TeamMember } from '@/services/PressService'
import { EmbeddedVideo } from '@/types/codegen-contentful'
import { useSafeTrack } from '@/utils/analytics'

const RELEASES_TO_SHOW = 5

function getFirstSentence(release: PressRelease): string | null {
  const text = release.excerpt || (release.body?.json ? documentToPlainTextString(release.body.json) : null)
  if (!text) return null
  const match = text.match(/^.+?[.!?](?:\s|$)/)
  return match ? match[0].trim() : text.split('\n')[0]?.trim() ?? null
}

interface PressViewProps {
  brands?: Project[]
  downloadables: Downloadable[]
  pressReleases: PressRelease[]
  project?: Project
  teamMembers: TeamMember[]
  videos?: EmbeddedVideo[]
}

export const PressView: React.FC<PressViewProps> = ({
  brands = [],
  downloadables,
  pressReleases,
  project,
  teamMembers,
  videos,
}) => {
  const [showMore, setShowMore] = useState([...new Array(teamMembers.length)].fill(false))
  const [showPrevious, setShowPrevious] = useState(false)
  const [copied, setCopied] = useState(false)
  const track = useSafeTrack()
  const logo = project?.logo?.[0]
    ? `v${project.logo[0].version}/${project.logo[0].public_id}.webp`
    : '/h_107,q_auto:best/v1637342010/angel-studios/logos/angel_only_white_2x_1.webp'

  const handleHidePrevious = () => {
    setShowPrevious(false)
  }

  const handleShowPrevious = () => {
    setShowPrevious(true)
  }

  const handleShowMore = useCallback(
    (index: number) => {
      const updatedArray = [...showMore]
      updatedArray[index] = true
      setShowMore(updatedArray)
    },
    [showMore, setShowMore],
  )

  const handleShowLess = useCallback(
    (index: number) => {
      const updatedArray = [...showMore]
      updatedArray[index] = false
      setShowMore(updatedArray)
    },
    [showMore, setShowMore],
  )

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText('angelstudios@pmkentertainment.com')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownloadableClick = useCallback(
    (downloadable: Downloadable) => {
      track('Press Downloadable Clicked', {
        project_slug: project?.slug ?? slugs.angelStudios,
        title: downloadable.title,
        type: downloadable.type,
      })
    },
    [project, track],
  )

  const pressInquiriesSection = (
    <div className="flex flex-col gap-2 rounded-lg border border-core-gray-200 bg-core-gray-100 py-10 text-center">
      <LinkButton
        className="w-fit self-center rounded-xl px-10 py-2 text-center"
        href="mailto:angelstudios@pmkentertainment.com?subject=Press%20Inquiry%20from%20'Media%20Outlet'"
        variant="oxide"
      >
        <ParagraphMD>Press Inquiries</ParagraphMD>
      </LinkButton>
      <div className="relative self-center">
        <button
          className="flex cursor-pointer items-center justify-center gap-1.5"
          onClick={handleCopyEmail}
          type="button"
        >
          <CopyIcon size={16} color="core-gray-600" />
          <ParagraphMD color="core-gray-600">angelstudios@pmkentertainment.com</ParagraphMD>
        </button>
        {copied && (
          <div className="absolute inset-x-0 top-full flex items-center justify-center gap-1 pt-1">
            <svg className="h-4 w-4 text-oxide-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            </svg>
            <ParagraphMD color="oxide-primary">Copied</ParagraphMD>
          </div>
        )}
      </div>
    </div>
  )

  const pressReleasesContent = pressReleases?.length > 0 && (
    <div className="flex flex-col gap-10">
      <HeadingLG as={AsH2} weight="bold">
        {project ? 'Press Releases' : 'Latest Press Releases'}
      </HeadingLG>
      <div className="flex flex-col">
        {pressReleases.slice(0, showPrevious ? pressReleases.length : RELEASES_TO_SHOW).map((release) => {
          const date = new Date(release.publishDate ?? release.sys.firstPublishedAt)
          const formattedDate = !project
            ? date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
            : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
          const summary = !project ? getFirstSentence(release) : null
          return (
            <div key={release.slug} className="flex flex-col gap-1 border-t border-core-gray-200 py-6">
              <ParagraphSM color="core-gray-600">{formattedDate}</ParagraphSM>
              <InternalLink href={`${paths.press.release}/${release.slug}`}>
                <ParagraphXL color="oxide" weight="bold">
                  {release.title}
                </ParagraphXL>
              </InternalLink>
              {summary && <ParagraphMD color="core-gray-600">{summary}</ParagraphMD>}
              {!project && (
                <LabelMD className="mt-1" weight="bold">
                  {release.project?.name?.toUpperCase() ?? 'ANGEL STUDIOS'}
                </LabelMD>
              )}
            </div>
          )
        })}
      </div>
      {pressReleases.length > RELEASES_TO_SHOW && (
        <div className="flex justify-center">
          {showPrevious ? (
            <Button
              className="rounded-lg border border-core-gray-950 px-6 py-2"
              onClick={handleHidePrevious}
              variant="plain"
            >
              <ParagraphMD weight="medium">View Less</ParagraphMD>
            </Button>
          ) : (
            <Button
              className="rounded-lg border border-core-gray-950 px-6 py-2"
              onClick={handleShowPrevious}
              variant="plain"
            >
              <ParagraphMD weight="medium">View All</ParagraphMD>
            </Button>
          )}
        </div>
      )}
    </div>
  )

  const brandsContent = brands.length > 0 && (
    <div className="flex flex-col gap-10">
      <HeadingLG as={AsH2} weight="bold">
        Brands
      </HeadingLG>
      <div className="flex flex-col gap-1">
        {brands
          .filter((brand) => brand.name && brand.slug !== slugs.dryBar)
          .toSorted((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
          .map((brand) => (
            <InternalLink key={brand.slug} href={`${paths.press.index}/${brand.slug}`}>
              <ParagraphXL color="oxide" underline>
                {brand.name}
              </ParagraphXL>
            </InternalLink>
          ))}
      </div>
    </div>
  )

  const ourTeamContent = (
    <>
      <HeadingLG as={AsH2} weight="bold">
        About Angel
      </HeadingLG>
      <ParagraphLG>
        Angel (NYSE: ANGX) is a media and technology company guided by 2 million grassroots Angel Guild paying members
        championing values-driven stories. Clearly expressing the kind of programming they crave, members of the Angel
        Guild act as virtual co-producers, greenlighting what films and television series get produced and distributed
        in theaters and on the Angel app. Propelled by this audience-first momentum, Angel has released more than 40
        films and 20 television series that amplify light, including &ldquo;Sound of Freedom,&rdquo; which earned more
        than $250 million at the worldwide box office. The company also has more than 6 billion views of its Dry Bar
        Comedy franchise, which has attracted some of the world&apos;s best-known comedians. For more information, visit{' '}
        <InternalLink href="https://www.angel.com" className="cursor-pointer text-oxide underline">
          www.angel.com
        </InternalLink>
        .
      </ParagraphLG>
      {teamMembers?.length > 0 && (
        <div className="flex flex-col gap-10">
          <HeadingLG as={AsH2} weight="bold">
            About Our Team
          </HeadingLG>
          <div className="flex flex-col gap-12">
            {teamMembers.map((member, index) => (
              <TeamMemberDisplay
                key={member.sys.id}
                member={member}
                onShowLess={() => handleShowLess(index)}
                onShowMore={() => handleShowMore(index)}
                showMore={showMore[index]}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )

  const tabNames = ['Press Releases', 'Brands', 'Our Team']

  return (
    <div className="bg-white pt-16">
      <PressHeader logo={logo} />
      <main className="md:w-7-12 mx-auto flex w-11/12 max-w-5xl flex-col gap-10 py-10">
        {pressInquiriesSection}
        {project?.pressCustomInfoSection?.json && (
          <LazyHydrate whenVisible>
            <section id="about">{documentToReactComponents(project?.pressCustomInfoSection?.json, options)}</section>
          </LazyHydrate>
        )}
        {project && downloadables?.length > 0 && (
          <div className="flex flex-col gap-10" id="media-kits">
            <HeadingLG as={AsH2} weight="bold">
              Media Kits
            </HeadingLG>
            <div className="flex flex-wrap gap-2">
              {downloadables.map((downloadable) => {
                return downloadable.image && downloadable.href ? (
                  <DocumentCard
                    authenticated={true}
                    href={downloadable.href}
                    image={downloadable.image}
                    key={downloadable.sys.id}
                    onClick={() => handleDownloadableClick(downloadable)}
                    returnTo={paths.press.index}
                    title={downloadable.title}
                    type={downloadable.type}
                    variant="light"
                  />
                ) : null
              })}
            </div>
          </div>
        )}
        {project && Boolean(videos?.length) && (
          <div className="flex flex-col gap-10" id="trailers">
            <HeadingLG as={AsH2} weight="bold">
              Official Videos
            </HeadingLG>
            <div className="flex flex-wrap gap-2">
              {videos?.map((video) => {
                return video.url ? <Video className="w-full" title={video.title as string} url={video.url} /> : null
              })}
            </div>
          </div>
        )}
        {!project ? (
          <TabGroup>
            <TabList className="flex gap-6 border-b border-core-gray-200">
              {tabNames.map((name) => (
                <Tab
                  key={name}
                  className={({ selected }) =>
                    classNames(
                      'pb-3 text-base font-medium outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent',
                      selected
                        ? 'border-b-2 border-oxide-primary font-bold text-core-gray-950'
                        : 'text-core-gray-500 hover:text-core-gray-700',
                    )
                  }
                >
                  {name}
                </Tab>
              ))}
            </TabList>
            <TabPanels className="pt-8">
              <TabPanel className="flex flex-col gap-10">{pressReleasesContent}</TabPanel>
              <TabPanel className="flex flex-col gap-10">{brandsContent}</TabPanel>
              <TabPanel className="flex flex-col gap-10">
                {ourTeamContent}
                {downloadables?.length > 0 && (
                  <LazyHydrate whenVisible>
                    <div className="flex flex-col gap-10" id="media-kits">
                      <HeadingLG as={AsH2} weight="bold">
                        Media Kits
                      </HeadingLG>
                      <div className="flex flex-wrap gap-2">
                        {downloadables.map((downloadable) => {
                          return downloadable.image && downloadable.href ? (
                            <DocumentCard
                              authenticated={true}
                              href={downloadable.href}
                              image={downloadable.image}
                              key={downloadable.sys.id}
                              onClick={() => handleDownloadableClick(downloadable)}
                              returnTo={paths.press.index}
                              title={downloadable.title}
                              type={downloadable.type}
                              variant="light"
                            />
                          ) : null
                        })}
                      </div>
                    </div>
                  </LazyHydrate>
                )}
              </TabPanel>
            </TabPanels>
          </TabGroup>
        ) : (
          <>
            {pressReleasesContent}
            {brandsContent}
            {teamMembers?.length > 0 && (
              <LazyHydrate whenVisible>
                <div className="flex flex-col gap-10">
                  <HeadingLG as={AsH2} weight="bold">
                    About the Cast and Filmmakers
                  </HeadingLG>
                  <div className="flex flex-col gap-12">
                    {teamMembers.map((member, index) => (
                      <TeamMemberDisplay
                        key={member.sys.id}
                        member={member}
                        onShowLess={() => handleShowLess(index)}
                        onShowMore={() => handleShowMore(index)}
                        showMore={showMore[index]}
                      />
                    ))}
                  </div>
                </div>
              </LazyHydrate>
            )}
          </>
        )}
      </main>
    </div>
  )
}
